import { StrictMode, Suspense, useLayoutEffect } from "react";
import { createRoot } from "react-dom/client";
import {
    createBrowserRouter,
    Navigate,
    RouterProvider,
} from "react-router-dom";
import App from "./App.tsx";
import "./styles/tailwind.css";

import { SWRConfig } from "swr";
import LoginRoute from "./components/core/auth/LoginRoute.tsx";
import LogoutRoute from "./components/core/auth/LogoutRoute.tsx";
import LoadingPage from "./components/core/loading/LoadingPage.tsx";
import {
    AUTH_STATE,
    BASENAME_ROOT,
    ROUTE_ABOUT_PATH,
    ROUTE_ADD_ITEM_PATH,
    ROUTE_ADMIN_CATEGORY_PATH,
    ROUTE_ADMIN_FILTER_PATH,
    ROUTE_ADMIN_ITEM_PATH,
    ROUTE_ADMIN_PATH,
    ROUTE_ADMIN_REPORT_PATH,
    ROUTE_ADMIN_USERS_PATH,
    ROUTE_AUTH_PATH,
    ROUTE_CHAT_PATH,
    ROUTE_EDIT_PROFILE_PATH,
    ROUTE_IN_CHAT_PATH,
    ROUTE_LANDING,
    ROUTE_LANDING_PATH,
    ROUTE_LOGIN_PATH,
    ROUTE_PRODUCT_PATH,
    ROUTE_PROFILE_PATH,
    ROUTE_REGISTER_PATH,
    ROUTE_SETTING_PATH,
    ROUTE_SHOP_PATH,
    ROUTE_VERIFY_PATH,
} from "./const/routes";
import { SubmitProvider } from "./context/context.submit";
import About from "./pages/About.tsx";
import {
    AdminCategoryPage,
    AdminFilterPage,
    AdminItemPage,
    AdminPage,
    AdminReportPage,
    AdminUsersPage,
} from "./pages/Admin/index.lazy.ts";
import AuthPage from "./pages/Auth/AuthPage.tsx";
import EnterOtp from "./pages/Auth/EnterOtp.tsx";
import RequestReset from "./pages/Auth/RequestReset.tsx";
import ChatPage from "./pages/ChatPage.tsx";
import { ItemPost } from "./pages/ItemForm/index.lazy.ts";
import ItemPage from "./pages/ItemPage.tsx";
import Login from "./pages/Login";
import OpenChat from "./pages/OpenChat.tsx";
import Registration from "./pages/Register.tsx";
import SettingPage from "./pages/SettingPage.tsx";
import ShopPage from "./pages/ShopPage.tsx";
import UserPage from "./pages/UserPage.tsx";
import UserPutPage from "./pages/UserPutPage.tsx";
import UserReport from "./pages/UserReport.tsx";
import Verify from "./pages/Verify.tsx";
import { UserRole } from "./types/User.ts";

const router = createBrowserRouter(
  [
    {
      path: BASENAME_ROOT,
      element: <App />,
      children: [
        {
          path: ROUTE_LANDING_PATH,
          element: <ShopPage />,

        },
        {
          path: ROUTE_SHOP_PATH,
          element: <ShopPage />,
        },
        {
          path: ROUTE_ADD_ITEM_PATH,
          element: (
            <LoginRoute>
              <Suspense fallback={<LoadingPage />}>
                <ItemPost />
              </Suspense>
            </LoginRoute>
          ),
        },
        {
          path: ROUTE_PRODUCT_PATH,
          element: <ItemPage />,
        },
        {
          path: ROUTE_LOGIN_PATH,
          element: (
            <LogoutRoute to={ROUTE_LANDING}>
              <Login />
            </LogoutRoute>
          ),
        },
        {
          path: ROUTE_REGISTER_PATH,
          element: (
            <LogoutRoute to={ROUTE_LANDING}>
              <Registration />
            </LogoutRoute>
          ),
        },
        {
          path: ROUTE_CHAT_PATH,
          element: (
            <LoginRoute>
              <SubmitProvider>
                <ChatPage />
              </SubmitProvider>
            </LoginRoute>
          ),
        },
        {
          path: ROUTE_IN_CHAT_PATH,
          element: (
            <LoginRoute>
              <SubmitProvider>
                <SWRConfig value={{ refreshInterval: 5000 }}>
                  <OpenChat />
                </SWRConfig>
              </SubmitProvider>
            </LoginRoute>
          ),
        },
        {
          path: ROUTE_EDIT_PROFILE_PATH,
          element: (
            <LoginRoute>
              <UserPutPage />
            </LoginRoute>
          ),
        },
        {
          path: ROUTE_PROFILE_PATH,
          element: <UserPage />,
        },
        {
          path: ROUTE_ADMIN_PATH,
          children: [
            {
              path: ROUTE_ADMIN_REPORT_PATH,
              element: <AdminReportPage />,
            },
            {
              path: ROUTE_ADMIN_USERS_PATH,
              element: <AdminUsersPage />,
            },
            {
              path: ROUTE_ADMIN_CATEGORY_PATH,
              element: <AdminCategoryPage />,
            },
            {
              path: ROUTE_ADMIN_FILTER_PATH,
              element: <AdminFilterPage />,
            },
            {
              path: ROUTE_ADMIN_ITEM_PATH,
              element: <AdminItemPage />,
            },
          ],
          element: (
            <Suspense fallback={<LoadingPage />}>
              <LoginRoute role={UserRole.Admin}>
                <AdminPage />
              </LoginRoute>
            </Suspense>
          ),
        },
        {
          path: ROUTE_SETTING_PATH,
          children: [{ path: "reports", element: <UserReport /> }],

          element: (
            <LoginRoute role={UserRole.Unverified}>
              <SettingPage />
            </LoginRoute>
          ),
        },
        {
          path: ROUTE_VERIFY_PATH,
          element: (
            <LoginRoute>
              <Verify />
            </LoginRoute>
          ),
        },
        {
          path: ROUTE_ABOUT_PATH,
          element: <About />,
        },
        {
          path: ROUTE_AUTH_PATH(),
          children: [
            {
              path: AUTH_STATE.reset,
              element: <RequestReset />,
            },
            {
              path: AUTH_STATE.otp,
              element: <EnterOtp />,
            },
          ],
          element: <AuthPage />,
        },
        {
          path: "*",
          element: <Navigate to={ROUTE_LANDING} />,
        },
      ],
    },
  ],
  { basename: BASENAME_ROOT },
);

const fadeOutLoading = () => {
  const loading = document.getElementById("loading");
  if (!loading) return;
  loading.style.opacity = "0";
  setTimeout(() => loading.remove(), 300);
};

const AppRoot = () => {
  useLayoutEffect(() => {
    fadeOutLoading();
  }, []);

  return <RouterProvider router={router} />;
};
document.getElementsByTagName("title").item(0)?.remove();
[
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
  'link[rel^="canonical"]',
].forEach((selector) => {
  document.querySelectorAll(selector).forEach((el) => el.remove());
});
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AppRoot />
  </StrictMode>,
);

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = event.data as unknown;
    if (
      data &&
      typeof data === "object" &&
      "type" in data &&
      (data as { type?: unknown }).type === "PUSH_NAVIGATE" &&
      "url" in data &&
      typeof (data as { url?: unknown }).url === "string"
    ) {
      const url = (data as { url: string }).url;
      try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.origin === window.location.origin) {
          const next = parsed.pathname + parsed.search + parsed.hash;
          if (
            window.location.pathname +
              window.location.search +
              window.location.hash !==
            next
          ) {
            window.history.pushState({}, "", next);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }
        }
      } catch {
        // ignore
      }
    }
  });
}
